import { cn } from "@repo/ui/lib/utils";
import { type KeyboardEvent, useState } from "react";
import { PILL_CLASS, pillStateClass } from "./pill-combobox";

interface DatePillProps {
  /** MM/DD/YYYY, or as much of it as has been typed. */
  text: string;
  onText: (text: string) => void;
  /** The blank's name, shown while empty; the format takes its place while typing. */
  label: string;
  /** Enter: the blank is answered, move on. */
  onAdvance: () => void;
  /** Escape: leave the phrase. */
  onExit: () => void;
}

/**
 * Digits only, the slashes put in as they are reached. A slash typed after a
 * single digit pads it, so "1/5/2025" types as it would on paper.
 */
function mask(raw: string): string {
  const parts = raw.replace(/[^\d/]/g, "").split("/");
  const padded = parts.map((part, index) =>
    index < 2 && index < parts.length - 1 && part.length === 1 ? `0${part}` : part,
  );
  const digits = padded.join("").slice(0, 8);
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4);
  const slash = raw.endsWith("/") && (digits.length === 2 || digits.length === 4) ? "/" : "";
  return [month, day, year].filter(Boolean).join("/") + slash;
}

/** One date blank in a phrase, typed as MM/DD/YYYY. */
export function DatePill({ text, onText, label, onAdvance, onExit }: DatePillProps) {
  const [focused, setFocused] = useState(false);
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onAdvance();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onExit();
    }
  };

  return (
    <input
      aria-label={label}
      autoComplete="off"
      spellCheck={false}
      inputMode="numeric"
      placeholder={focused ? "MM/DD/YYYY" : label}
      value={text}
      onChange={(event) => onText(mask(event.target.value))}
      onKeyDown={onKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={cn(PILL_CLASS, pillStateClass(!text), "tabular-nums")}
    />
  );
}
